import React from "react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useDrawerKeyboardFit } from "@/hooks/useDrawerKeyboardFit";
import "./DocumentCommentDrawer.css";

interface DocumentCommentDrawerProps {
  open: boolean;
  onClose: () => void;
  initialValue: string;
  onDone: (value: string) => Promise<void>;
  loading?: boolean;
  title?: string;
}

const DocumentCommentDrawer: React.FC<DocumentCommentDrawerProps> = ({
  open,
  onClose,
  initialValue,
  onDone,
  loading = false,
  title = "Add Comment",
}) => {
  const [value, setValue] = React.useState(initialValue);
  const [submitting, setSubmitting] = React.useState(false);

  // Sit the drawer just above the keyboard (shared FooterDrawer left as-is).
  // Returns whether the keyboard is currently up.
  const keyboardOpen = useDrawerKeyboardFit(open, "document-comment-drawer");

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue, open]);

  React.useEffect(() => {
    if (open) {
      document.body.classList.remove("drawer-open");
      document.body.classList.add("comment-drawer-open");

      const timer = setTimeout(() => {
        const textarea = document.querySelector(
          ".document-comment-drawer textarea"
        ) as HTMLTextAreaElement;
        if (textarea && textarea instanceof HTMLTextAreaElement) {
          textarea.focus();
        }
      }, 300);

      return () => {
        clearTimeout(timer);
        document.body.classList.remove("comment-drawer-open");
        document.body.classList.add("drawer-open");
      };
    }
  }, [open]);

  const handleDone = async () => {
    setSubmitting(true);
    try {
      await onDone(value);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const isDisabled = loading || submitting || value === initialValue;

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title={title}
      className="document-comment-drawer"
      allowKeyboard={true}
    >
      <div className="document-comment-drawer-body">
        <MTextArea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Add a comment about this document..."
          rows={4}
          autoFocus
          className="w-full max-w-full"
          maxLength={250}
        />
        {/* Hide the Done button while the keyboard is open to free up
            vertical space — same pattern as NoteDrawer. It reappears the
            moment the keyboard dismisses. */}
        {!keyboardOpen && (
          <div className="document-comment-drawer-done-row">
            <MButton
              size="lg"
              variant="filled"
              className="document-comment-drawer-done-btn"
              disabled={isDisabled}
              onClick={handleDone}
            >
              Done
            </MButton>
          </div>
        )}
      </div>
    </FooterDrawer>
  );
};

export default DocumentCommentDrawer;
